import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  TextField, 
  List, 
  ListItem, 
  ListItemText, 
  Paper,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
  alpha
} from '@mui/material';
import { 
  Send as SendIcon,
  Person as PersonIcon,
  SmartToy as BotIcon,
  Delete as DeleteIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { chatbotQuery } from '../services/api';
import SelectedAccordsGrid from './SelectedAccordsGrid';

function Chatbot({ selectedAccords }) {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const theme = useTheme();

  if (!isAuthenticated) {
    return (
      <Card sx={{ textAlign: 'center', mt: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <BotIcon sx={{ fontSize: 64, color: theme.palette.primary.main, mb: 2 }} />
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Acesso Restrito
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Para acessar o Chatbot, você precisa estar autenticado no sistema.
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => loginWithRedirect()}
            startIcon={<PersonIcon />}
            size="large"
          >
            Fazer Login
          </Button>
        </CardContent>
      </Card>
    );
  }

  const handleSendMessage = async () => {
    if (input.trim() === '') return;

    const userMessage = { role: 'user', content: input };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setInput('');
    setLoading(true);

    try {
      // Enhanced debugging for API call
      const apiPayload = {
        user_message: input,
        conversation_history: messages,
        filtered_data_ids: selectedAccords.map(accord => accord.id),
      };
      
      console.log('🤖 CHATBOT API DEBUG:');
      console.log('📊 Selected accords count:', selectedAccords.length);
      console.log('🔍 Selected accord IDs:', apiPayload.filtered_data_ids);
      console.log('📤 Full API payload:', JSON.stringify(apiPayload, null, 2));
      
      if (selectedAccords.length > 0) {
        console.log('📋 Sample accord object:', selectedAccords[0]);
      }
      
      const response = await chatbotQuery(apiPayload);
      
      console.log('📨 CHATBOT API RESPONSE:');
      console.log('✅ Response received:', response);
      
      // Check if the response indicates missing context
      if (response.assistant_message.includes('Não tenho dados disponíveis') ||
          response.assistant_message.includes('Não há casos disponíveis') ||
          response.assistant_message.includes('não há casos relevantes')) {
        console.log('❌ BACKEND ISSUE DETECTED: Chatbot cannot access selected cases');
        console.log('💡 This confirms the backend filter_data_by_id method is not working');
      } else {
        console.log('✅ SUCCESS: Chatbot successfully processed the selected cases');
      }
      
      setMessages((prevMessages) => [...prevMessages, { role: 'assistant', content: response.assistant_message }]);
    } catch (error) {
      console.error('Error sending message to chatbot:', error);
      setMessages((prevMessages) => [...prevMessages, { 
        role: 'assistant', 
        content: 'Desculpe, ocorreu um erro ao processar sua mensagem. Tente novamente.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = () => {
    setMessages([]);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: 700,
            mb: 1,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <BotIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
          Assistente Jurídico
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Converse com nossa IA especializada em decisões do CRSNSP
        </Typography>
      </Box>

      {/* Selected Accords Info */}
      {selectedAccords.length === 0 && (
        <Alert 
          severity="info" 
          sx={{ mb: 3, borderRadius: 2 }}
          icon={<InfoIcon />}
        >
          <Typography variant="body2">
            <strong>Dica:</strong> Selecione alguns acórdãos no mapa para obter respostas mais precisas e contextualizadas.
          </Typography>
        </Alert>
      )}

      {selectedAccords.length > 0 && (
        <Alert 
          severity="success" 
          sx={{ mb: 3, borderRadius: 2 }}
        >
          <Typography variant="body2">
            Usando <strong>{selectedAccords.length} acórdão(s)</strong> como contexto para suas perguntas.
          </Typography>
        </Alert>
      )}

      {/* Chat Interface */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Conversa
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip 
                label={`${messages.length} mensagens`}
                size="small"
                variant="outlined"
              />
              {messages.length > 0 && (
                <IconButton 
                  size="small" 
                  onClick={clearHistory}
                  color="error"
                  sx={{ ml: 1 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>

          <Paper 
            elevation={0}
            sx={{ 
              height: 400, 
              overflowY: 'auto', 
              mb: 2, 
              p: 2,
              backgroundColor: alpha(theme.palette.background.default, 0.3),
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            {messages.length === 0 ? (
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  textAlign: 'center',
                }}
              >
                <BotIcon sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Comece uma conversa
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Faça perguntas sobre os acórdãos selecionados ou questões jurídicas relacionadas
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {messages.map((msg, index) => (
                  <ListItem 
                    key={index} 
                    sx={{ 
                      justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                      alignItems: 'flex-start',
                      pb: 2,
                    }}
                  >
                    <Box 
                      sx={{ 
                        display: 'flex',
                        flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                        alignItems: 'flex-start',
                        gap: 1,
                        maxWidth: '80%',
                      }}
                    >
                      <Avatar 
                        sx={{ 
                          width: 32, 
                          height: 32,
                          backgroundColor: msg.role === 'user' ? theme.palette.primary.main : theme.palette.secondary.main,
                        }}
                      >
                        {msg.role === 'user' ? <PersonIcon fontSize="small" /> : <BotIcon fontSize="small" />}
                      </Avatar>
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          backgroundColor: msg.role === 'user' 
                            ? alpha(theme.palette.primary.main, 0.1)
                            : theme.palette.background.paper,
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        }}
                      >
                        <Typography 
                          variant="body2"
                          sx={{ 
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {msg.content}
                        </Typography>
                      </Paper>
                    </Box>
                  </ListItem>
                ))}
                {loading && (
                  <ListItem sx={{ justifyContent: 'flex-start', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Avatar 
                        sx={{ 
                          width: 32, 
                          height: 32,
                          backgroundColor: theme.palette.secondary.main,
                        }}
                      >
                        <BotIcon fontSize="small" />
                      </Avatar>
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                          Digitando...
                        </Typography>
                      </Paper>
                    </Box>
                  </ListItem>
                )}
              </List>
            )}
          </Paper>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Digite sua pergunta sobre os acórdãos..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              multiline
              maxRows={3}
              disabled={loading}
            />
            <Button 
              variant="contained" 
              onClick={handleSendMessage} 
              disabled={loading || input.trim() === ''}
              sx={{ 
                minWidth: 56,
                height: 56,
              }}
            >
              <SendIcon />
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Selected Accords Grid */}
      <SelectedAccordsGrid
        selectedAccords={selectedAccords}
        onRemoveAccord={() => {}} // Readonly in this context
      />
    </Box>
  );
}

export default Chatbot;
